//const BET_TYPES = new Set(["betPerLine", "totalBet"]);
const BET_TYPES = new Set(["totalBet"]);

export const ALLOWED_CURRENCIES = Object.freeze(["USD", "COP", "CLP", "PEN", "BRL", "ARS", "TND", "MXN", "VES", "UYU", "DOP", "HTG", "PYG", "TRY", "EUR", "RUS", "IRN"]);
export const FREE_SPINS_BONUS_TYPES = Object.freeze([{"value": "F", "description": "FULL SCATTER"}]);

const BET_SCALE_QUERY_ORDER = ["clientCode", "gameIDs", "currencies"];

export function buildPragmaticBetScalesQuery(params = {}) {
  return BET_SCALE_QUERY_ORDER.flatMap((key) => {
    const value = params?.[key];
    if (value === undefined || value === null || value === "") return [];
    const tokens = Array.isArray(value) ? value : String(value).split(",");
    const encoded = tokens.map((token) => encodeURIComponent(String(token).trim())).filter(Boolean).join(",");
    return encoded ? [`${key}=${encoded}`] : [];
  }).join("&");
}

export function buildGameIdsQuery(gameIds) {
  return buildPragmaticBetScalesQuery({ gameIDs: gameIds });
}

export function buildBetScaleIndex(response) {
  const payload = response?.data?.gameList ? response.data : response;
  const index = new Map();
  for (const game of Array.isArray(payload?.gameList) ? payload.gameList : []) {
    const currencies = new Map();
    for (const scale of Array.isArray(game?.betScaleList) ? game.betScaleList : []) {
      currencies.set(normalizeCurrency(scale?.currency), {
        betPerLine: normalizeScaleList(scale?.betPerLineScales),
        totalBet: normalizeScaleList(scale?.totalBetScales)
      });
    }
    index.set(String(game?.gameID ?? ""), currencies);
  }
  return index;
}

export function getBetScaleOptions(index, gameId, currency, betType) {
  if (!(index instanceof Map) || !BET_TYPES.has(betType)) return [];
  return index.get(String(gameId))?.get(normalizeCurrency(currency))?.[betType] ?? [];
}

export function getAvailableCurrencyOptions(currencies, games, gameId, rowIndex) {
  const game = Array.isArray(games) ? games.find((item) => String(item?.gameId) === String(gameId)) : undefined;
  const currentCurrency = normalizeCurrency(game?.betValues?.[rowIndex]?.currency);
  const selectedBySiblings = new Set((game?.betValues ?? [])
    .filter((_, index) => index !== rowIndex)
    .map((row) => normalizeCurrency(row?.currency))
    .filter(Boolean));
  return (Array.isArray(currencies) ? currencies : []).filter((currency) => {
    const normalized = normalizeCurrency(currency);
    return normalized === currentCurrency || !selectedBySiblings.has(normalized);
  });
}
export function reconcileBetConfigurationValue(value, options) {
  if (value === "" || value === null || value === undefined) return "";
  return options.some((option) => Number(option) === Number(value)) ? value : "";
}

function normalizeScaleList(scales) {
  if (!Array.isArray(scales)) return [];
  return [...new Set(scales.filter((scale) => typeof scale === "number" && Number.isFinite(scale) && scale > 0))];
}
export function createBetConfiguration(betType = "betPerLine") {
  return { currency: "", betType: normalizeBetType(betType), value: "" };
}

export function reconcileGameConfigurations(selectedIds, current, availableGames, betType = "betPerLine") {
  const normalizedBetType = normalizeBetType(betType);
  const existing = new Map(current.map((game) => [String(game.gameId), game]));
  const available = new Map(availableGames.map((game) => [String(game.gameID ?? game.gameId), game]));
  return selectedIds.map((selectedGameId) => {
    const gameId = String(selectedGameId);
    const catalogGame = available.get(gameId);
    const game = existing.get(gameId) ?? {
      gameId,
      gameName: catalogGame?.gameName ?? catalogGame?.name ?? "",
      lines: catalogGame?.lines ?? null,
      betValues: [createBetConfiguration(normalizedBetType)]
    };
    return {
      ...game,
      gameName: catalogGame?.gameName ?? catalogGame?.name ?? game.gameName,
      lines: catalogGame?.lines ?? game.lines ?? null,
      betValues: game.betValues.map((row) => ({ ...row, betType: normalizedBetType }))
    };
  });
}

export function addBetConfiguration(games, gameId, betType = "betPerLine") {
  return games.map((game) => game.gameId === gameId
    ? { ...game, betValues: [...game.betValues, createBetConfiguration(betType)] }
    : game);
}

export function removeBetConfiguration(games, gameId, rowIndex) {
  return games.map((game) => {
    if (game.gameId !== gameId || game.betValues.length === 1) return game;
    return { ...game, betValues: game.betValues.filter((_, index) => index !== rowIndex) };
  });
}

export function updateBetConfiguration(games, gameId, rowIndex, field, value) {
  return games.map((game) => game.gameId === gameId ? {
    ...game,
    betValues: game.betValues.map((row, index) => index === rowIndex
      ? { ...row, [field]: field === "currency" ? normalizeCurrency(value) : value }
      : row)
  } : game);
}

export function validateGameConfigurations(
  games,
  allowedCurrencies = ALLOWED_CURRENCIES,
) {
  const allowedCurrencySet = currencySet(allowedCurrencies);
  const requestBetTypes = new Set();
  const errors = validateGameList(games, (row, context, errors, combinations) => {
    const hasOwnCurrency = Object.prototype.hasOwnProperty.call(row ?? {}, "currency");
    const currency = validateCurrency(
      hasOwnCurrency ? row.currency : undefined,
      context,
      errors,
      allowedCurrencySet,
    );
    const betType = row?.betType;
    if (!BET_TYPES.has(betType)) errors.push(`${context}: invalid bet value type`);
    else requestBetTypes.add(betType);
    validateEditablePositiveValue(row?.value, context, errors);
    if (currency) validateUniqueCurrency(combinations, currency, context, errors);
  });
  if (requestBetTypes.size > 1) {
    errors.unshift("Toda la campaña debe utilizar únicamente betPerLine o totalBet, no ambos");
  }
  return errors;
}

export function buildGamesPayload(games) {
  return {
    gameList: games.map((game) => ({
      gameId: String(game.gameId).trim(),
      gameName: String(game.gameName ?? "").trim(),
      betValues: game.betValues.map((row) => ({
        [row.betType]: Number(row.value),
        currency: normalizeCurrency(row.currency)
      }))
    }))
  };
}

export function validateGamesPayload(
  games,
  allowedCurrencies = ALLOWED_CURRENCIES,
) {
  const allowedCurrencySet = currencySet(allowedCurrencies);
  const requestBetTypes = new Set();
  const errors = validateGameList(games?.gameList, (row, context, errors, combinations) => {
    const hasOwnCurrency = Object.prototype.hasOwnProperty.call(row ?? {}, "currency");
    const currency = validateCurrency(
      hasOwnCurrency ? row.currency : undefined,
      context,
      errors,
      allowedCurrencySet,
    );
    const hasBetPerLine = Object.prototype.hasOwnProperty.call(row ?? {}, "betPerLine");
    const hasTotalBet = Object.prototype.hasOwnProperty.call(row ?? {}, "totalBet");
    if (hasBetPerLine === hasTotalBet) {
      errors.push(`${context}: exactly one of betPerLine or totalBet is required`);
      return;
    }
    const betType = hasBetPerLine ? "betPerLine" : "totalBet";
    requestBetTypes.add(betType);
    validatePayloadPositiveValue(row[betType], context, errors);
    if (currency) validateUniqueCurrency(combinations, currency, context, errors);
  });
  if (requestBetTypes.size > 1) {
    errors.unshift("Toda la solicitud debe utilizar únicamente betPerLine o totalBet, no ambos");
  }
  return errors;
}

function validateGameList(games, validateRow) {
  const errors = [];
  if (!Array.isArray(games) || games.length === 0) {
    errors.push("At least one game is required");
    return errors;
  }
  const gameIds = new Set();
  for (const [gameIndex, game] of games.entries()) {
    const gameId = String(game?.gameId ?? "").trim();
    const gameContext = gameId || `Game ${gameIndex + 1}`;
    if (!gameId) errors.push(`${gameContext}: gameId is required`);
    else if (gameIds.has(gameId)) errors.push(`${gameContext}: duplicate gameId`);
    else gameIds.add(gameId);
    if (!String(game?.gameName ?? "").trim()) errors.push(`${gameContext}: gameName is required`);
    if (!Array.isArray(game?.betValues) || game.betValues.length === 0) {
      errors.push(`${gameContext}: at least one bet value is required`);
      continue;
    }
    const combinations = new Set();
    for (const [index, row] of game.betValues.entries()) validateRow(row, `${gameContext} row ${index + 1}`, errors, combinations);
  }
  return errors;
}

function normalizeCurrency(currency) {
  return String(currency ?? "").trim().toUpperCase();
}

function normalizeBetType(betType) {
  return BET_TYPES.has(betType) ? betType : "betPerLine";
}

function currencySet(currencies) {
  return new Set(
    (Array.isArray(currencies) ? currencies : [])
      .map(normalizeCurrency)
      .filter(Boolean),
  );
}

function validateCurrency(value, context, errors, allowedCurrencySet) {
  const currency = normalizeCurrency(value);
  if (!currency) errors.push(`${context}: currency is required`);
  else if (!allowedCurrencySet.has(currency)) errors.push(`${context}: unsupported currency ${currency}`);
  return allowedCurrencySet.has(currency) ? currency : "";
}

function validateEditablePositiveValue(value, context, errors) {
  const isSupportedType = typeof value === "number" || typeof value === "string";
  const numericValue = isSupportedType && String(value).trim() !== "" ? Number(value) : Number.NaN;
  if (!Number.isFinite(numericValue) || numericValue <= 0) errors.push(`${context}: bet value must be greater than zero`);
}

function validatePayloadPositiveValue(value, context, errors) {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) errors.push(`${context}: bet value must be greater than zero`);
}

function validateUniqueCurrency(currencies, currency, context, errors) {
  if (currencies.has(currency)) errors.push(`${context}: duplicate currency`);
  else currencies.add(currency);
}

